import React, { useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { checkIfProductionEnvironment } from 'src/lib/checkIfProductionEnvironment'
import { AdvancedApiError, advancedPost } from '../api/client'
import { AdvancedTrackDefinition } from '../api/types'
import { useTrackCatalog } from '../api/useTrackCatalog'
import { PROVIDER_ROLES } from '../currentRole'
import { TrackStatus } from '../dashboard/tracks/trackTransitions'
import SectionCard from '../components/SectionCard'
import { advancedColors } from '../theme'

type SeedStatus = { kind: 'idle' | 'pending' | 'seeded' }

type TrackChoice = TrackStatus | 'none'

type SeededTrack = { trackKey: string; status: TrackStatus }

const CARE_TEAM_ROLES = PROVIDER_ROLES.filter((role) => role.id !== 'admin')

const TRACK_CHOICES: { id: TrackChoice; label: string }[] = [
  { id: 'none', label: 'Not enrolled' },
  { id: 'intake', label: 'Intake' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'active', label: 'Active' },
  { id: 'stalled', label: 'Stalled' },
  { id: 'completed', label: 'Completed' },
  { id: 'closed', label: 'Closed without completion' },
]

const OPEN_STATUSES: readonly TrackStatus[] = ['intake', 'scheduled', 'active', 'stalled']

const DEFAULT_CHOICES: Record<string, TrackChoice> = {
  therapy: 'active',
  'optimal-nutrition': 'active',
  'activity-beginner': 'completed',
}

const FAILURE_MESSAGES: Record<number, string> = {
  400: 'Check the patient uid and the seeded tracks.',
  401: 'Your session has no admin claim. Sign in again as an admin; switching access level drops it.',
  404: 'This Advanced service has no test-data route.',
  409: 'That patient already has Advanced data, so nothing was written.',
}

const seedTestPatient = (
  uid: string,
  body: { role: string; tracks: SeededTrack[]; completions: boolean; reflections: boolean }
) => advancedPost(`/test-data/patients/${encodeURIComponent(uid)}`, body)

const failureMessage = (error: unknown): string =>
  (error instanceof AdvancedApiError && FAILURE_MESSAGES[error.status]) || 'Seeding failed. Try again.'

const toChoice = (value: string): TrackChoice => TRACK_CHOICES.find((choice) => choice.id === value)?.id ?? 'none'

const choicesFor = (track: AdvancedTrackDefinition) =>
  track.startsOnAcceptance ? TRACK_CHOICES.filter((choice) => choice.id !== 'scheduled') : TRACK_CHOICES

const seededTracks = (catalog: AdvancedTrackDefinition[], choices: Record<string, TrackChoice>): SeededTrack[] =>
  catalog.flatMap((track) => {
    const choice = choices[track.trackKey] ?? 'none'

    return choice === 'none' ? [] : [{ trackKey: track.trackKey, status: choice }]
  })

// Mirrors the service's one-open-track-per-module refusal.
const conflictingTitles = (catalog: AdvancedTrackDefinition[], tracks: SeededTrack[]): string[] => {
  const open = tracks.flatMap((seeded) => {
    const track = catalog.find((candidate) => candidate.trackKey === seeded.trackKey)

    return track?.module && OPEN_STATUSES.includes(seeded.status) ? [track] : []
  })

  return open
    .filter((track) => open.some((other) => other !== track && other.module === track.module))
    .map((track) => track.title)
}

const summaryOf = (tracks: SeededTrack[], completions: boolean, reflections: boolean): string =>
  [
    `${tracks.length} ${tracks.length === 1 ? 'track' : 'tracks'}`,
    completions && 'completed lessons',
    reflections && 'reflections',
  ]
    .filter(Boolean)
    .join(', ')

const AdvancedTestPatientForm = () => {
  const catalog = useTrackCatalog()
  const [uid, setUid] = useState('')
  const [role, setRole] = useState('therapist')
  const [choices, setChoices] = useState(DEFAULT_CHOICES)
  const [completions, setCompletions] = useState(true)
  const [reflections, setReflections] = useState(true)
  const [status, setStatus] = useState<SeedStatus>({ kind: 'idle' })
  const { addToast } = useToasts()

  if (checkIfProductionEnvironment()) {
    return null
  }

  const tracks = seededTracks(catalog, choices)
  const conflicts = conflictingTitles(catalog, tracks)
  const canSeed = uid.trim() !== '' && catalog.length > 0 && conflicts.length === 0 && status.kind !== 'pending'

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setStatus({ kind: 'pending' })
    try {
      await seedTestPatient(uid.trim(), { role, tracks, completions, reflections })
      setStatus({ kind: 'seeded' })
    } catch (error) {
      setStatus({ kind: 'idle' })
      addToast(failureMessage(error), { appearance: 'error' })
    }
  }

  return (
    <SectionCard
      title="Test patient"
      help="Create sample Advanced data and add yourself to a test patient’s care team. You must already be on their Therapy/Coaching care team."
      collapsible
    >
      {/* The dashboard theme puts a top margin on every FormControl. */}
      <Box component="form" onSubmit={submit} sx={{ '& .MuiFormControl-root': { mt: 0 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 2, alignItems: 'start' }}>
          <TextField
            fullWidth
            id="advanced-test-patient-uid"
            label="Patient uid"
            helperText="Use the uid from the end of the participant URL. The patient must have no Advanced data."
            size="small"
            value={uid}
            onChange={(event) => setUid(event.target.value)}
          />
          <TextField
            select
            fullWidth
            id="advanced-test-patient-role"
            label="Your role"
            size="small"
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            {CARE_TEAM_ROLES.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Accordion variant="outlined" disableGutters sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
              Seeded data
            </Typography>
            <Typography variant="body2" sx={{ color: advancedColors.inkMuted, mr: 1 }}>
              {summaryOf(tracks, completions, reflections)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                {catalog.map((track) => (
                  <TextField
                    key={track.trackKey}
                    select
                    fullWidth
                    id={`advanced-test-patient-track-${track.trackKey}`}
                    label={track.title}
                    size="small"
                    value={choices[track.trackKey] ?? 'none'}
                    onChange={(event) =>
                      setChoices((previous) => ({ ...previous, [track.trackKey]: toChoice(event.target.value) }))
                    }
                  >
                    {choicesFor(track).map((choice) => (
                      <MenuItem key={choice.id} value={choice.id}>
                        {choice.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
              </Box>
              {conflicts.length > 0 && (
                <Alert severity="warning">
                  {conflicts.join(' and ')} share a module, and only one track per module can be open at a time.
                </Alert>
              )}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox checked={completions} onChange={(event) => setCompletions(event.target.checked)} />
                  }
                  label="Completed lessons"
                />
                <FormControlLabel
                  control={
                    <Checkbox checked={reflections} onChange={(event) => setReflections(event.target.checked)} />
                  }
                  label="Reflections"
                />
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Button type="submit" variant="contained" disabled={!canSeed} sx={{ mt: 2 }}>
          Seed
        </Button>
        {status.kind === 'seeded' && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                Reload
              </Button>
            }
          >
            Seeded. Reload to see them on your caseload.
          </Alert>
        )}
      </Box>
    </SectionCard>
  )
}

export default AdvancedTestPatientForm
